import { useCallback, useRef, useState } from 'react';
import { AxiosError, AxiosProgressEvent } from 'axios';
import type { FileUploadValidationError } from '@/components/Common/FileUpload';
import { FILE_UPLOAD_URL } from '@/constant/apiList';
import {
  ATTACHMENT_FILENAME_MAX_LENGTH,
  ATTACHMENT_UPLOAD_VARIANT_CONFIG,
} from '@/constant/approval';
import {
  AttachmentUploadProgress,
  AttachmentUploadResult,
  AttachmentUploadVariant,
} from '@/types/approval';
import { jwtInstance } from '@/utils/api/jwt/instance';

/**
 * 클라이언트 검증 실패 코드는 공통 `FileUploadValidationError` 와 정합 유지.
 * 그 외 코드(NETWORK/ABORTED/UNKNOWN)는 업로드 단계에서만 발생.
 */
export type AttachmentUploadErrorCode =
  | FileUploadValidationError
  | 'NETWORK'
  | 'ABORTED'
  | 'UNKNOWN';

/** 표시용 번역 키와 보간값 — 소비처가 `t(key, options)` 로 쓴다 */
export type AttachmentUploadErrorI18n = {
  key: string;
  options?: Record<string, unknown>;
};

export class AttachmentUploadError extends Error {
  code: AttachmentUploadErrorCode;
  cause?: unknown;
  /**
   * `message` 는 번역 전 한국어 그대로 유지(인테리어 등 기존 소비처 호환).
   * 화면에 문구를 띄우는 소비처는 이 필드로 번역할 것.
   */
  i18n?: AttachmentUploadErrorI18n;
  constructor(
    code: AttachmentUploadErrorCode,
    message: string,
    cause?: unknown,
    i18n?: AttachmentUploadErrorI18n,
  ) {
    super(message);
    this.name = 'AttachmentUploadError';
    this.code = code;
    this.cause = cause;
    this.i18n = i18n;
  }
}

export type UseFileUploadOptions = {
  onSuccess?: (result: AttachmentUploadResult, file: File) => void;
  onError?: (error: AttachmentUploadError, file: File) => void;
  onProgress?: (progress: AttachmentUploadProgress, file: File) => void;
  /** 진행 중 업로드를 외부에서 취소할 때 연결하는 AbortSignal */
  signal?: AbortSignal;
};

const getExtension = (fileName: string): string => {
  const idx = fileName.lastIndexOf('.');
  return idx >= 0 ? fileName.slice(idx + 1).toLowerCase() : '';
};

const validate = (
  file: File,
  variant: AttachmentUploadVariant,
): AttachmentUploadError | null => {
  const config = ATTACHMENT_UPLOAD_VARIANT_CONFIG[variant];
  if (file.size <= 0) {
    return new AttachmentUploadError(
      'EMPTY_FILE',
      '0바이트 파일은 업로드할 수 없습니다.',
      undefined,
      { key: '0바이트 파일은 업로드할 수 없습니다.' },
    );
  }
  if (file.size > config.maxSize) {
    const mb = Math.floor(config.maxSize / 1024 / 1024);
    return new AttachmentUploadError(
      'OVER_MAX_SIZE',
      `파일당 최대 ${mb}MB까지 업로드 가능합니다.`,
      undefined,
      { key: '파일당 최대 {{mb}}MB까지 업로드 가능합니다.', options: { mb } },
    );
  }
  if (file.name.length > ATTACHMENT_FILENAME_MAX_LENGTH) {
    return new AttachmentUploadError(
      'NAME_TOO_LONG',
      `파일명이 ${ATTACHMENT_FILENAME_MAX_LENGTH}자를 초과합니다.`,
      undefined,
      {
        key: '파일명이 {{max}}자를 초과합니다.',
        options: { max: ATTACHMENT_FILENAME_MAX_LENGTH },
      },
    );
  }
  if (config.allowedExtensions.size > 0) {
    const ext = getExtension(file.name);
    if (!config.allowedExtensions.has(ext)) {
      const formats =
        config.accept || Array.from(config.allowedExtensions).join(', ');
      return new AttachmentUploadError(
        'INVALID_EXTENSION',
        `허용되지 않는 형식입니다. (${formats})`,
        undefined,
        {
          key: '허용되지 않는 형식입니다. ({{formats}})',
          options: { formats },
        },
      );
    }
  }
  return null;
};

/**
 * 단일 파일 업로드 훅.
 * - variant별 client-side 제약(사이즈/확장자/단건/다중) 검증 후 POST /api/v1/file/upload 호출
 * - progress(0~100), isUploading 상태 노출. 다중 파일은 호출부에서 순회하며 upload(file)를 반복 호출
 * - onSuccess/onError/onProgress 콜백 + Promise<AttachmentUploadResult> 양쪽 모두 지원
 * - abort()로 진행 중 업로드 취소 가능 (단일 인스턴스 기준 마지막 호출 대상)
 */
const useFileUpload = (
  variant: AttachmentUploadVariant,
  options?: UseFileUploadOptions,
) => {
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const upload = useCallback(
    async (file: File): Promise<AttachmentUploadResult> => {
      const validationError = validate(file, variant);
      if (validationError) {
        options?.onError?.(validationError, file);
        throw validationError;
      }

      const config = ATTACHMENT_UPLOAD_VARIANT_CONFIG[variant];
      const controller = new AbortController();
      abortRef.current = controller;

      // 외부 signal 연동
      if (options?.signal) {
        if (options.signal.aborted) controller.abort();
        else options.signal.addEventListener('abort', () => controller.abort());
      }

      const formData = new FormData();
      formData.append('downloadType', config.downloadType);
      formData.append('file', file);
      if (config.downloadExpirationSeconds) {
        formData.append(
          'downloadExpirationSeconds',
          String(config.downloadExpirationSeconds),
        );
      }

      setProgress(0);
      setIsUploading(true);

      try {
        const res = await jwtInstance.post<{ data: AttachmentUploadResult }>(
          FILE_UPLOAD_URL,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 0, // 대용량 업로드 — 타임아웃 해제
            signal: controller.signal,
            onUploadProgress: (e: AxiosProgressEvent) => {
              const total = e.total ?? file.size;
              const loaded = e.loaded;
              const percent =
                total > 0
                  ? Math.min(100, Math.round((loaded / total) * 100))
                  : 0;
              setProgress(percent);
              options?.onProgress?.({ percent, loaded, total }, file);
            },
          },
        );

        const result = res.data?.data;
        if (!result?.uuid) {
          throw new AttachmentUploadError(
            'UNKNOWN',
            '업로드 응답에 uuid가 없습니다.',
            res.data,
            // 내부 사정이라 사용자에겐 일반 실패 문구로 보여준다
            { key: '업로드에 실패했습니다.' },
          );
        }
        options?.onSuccess?.(result, file);
        return result;
      } catch (err) {
        const code: AttachmentUploadErrorCode =
          err instanceof AttachmentUploadError
            ? err.code
            : (err as AxiosError)?.code === 'ERR_CANCELED' ||
              controller.signal.aborted
            ? 'ABORTED'
            : 'NETWORK';
        const wrapped =
          err instanceof AttachmentUploadError
            ? err
            : new AttachmentUploadError(
                code,
                (err as Error)?.message || '업로드에 실패했습니다.',
                err,
              );
        options?.onError?.(wrapped, file);
        throw wrapped;
      } finally {
        setIsUploading(false);
        abortRef.current = null;
      }
    },
    [variant, options],
  );

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { upload, isUploading, progress, abort };
};

export default useFileUpload;
